/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../core/components';
import { ClusterService, LicenceService, LockService, PromptService, UpdatesService } from './shared/services/utilities';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MenuConfigModel } from '../../core/models';
import {
    LanguageService,
    ModalService,
    NavigationService,
    RbacService,
    SocketService,
    UserService
} from '../../core/services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-loadbalancer',
    templateUrl: './loadbalancer.component.html',
})

export class LoadbalancerComponent extends BaseComponent implements OnInit {
    /**
     * Client details ref.
     */
    @ViewChild('clientDetailsTrigger', { static: true }) public clientDetailsTrigger: ElementRef;

    /**
     * Main content ref.
     */
    @ViewChild('mainContent', { static: true }) public mainContent: ElementRef;

    /**
     * Lb menu config.
     */
    public menuConfig: MenuConfigModel = <MenuConfigModel>{};

    /**
     * Status of the cluster e.g ok, error.
     */
    public clusterStatus: string;

    /**
     * Local node status
     */
    public nodeStatus: string;

    /**
     * Store the hint text for cluster health icon.
     */
    public hintText: string;

    constructor(
        private clusterService: ClusterService,
        private languageService: LanguageService,
        private licenceService: LicenceService,
        private lockService: LockService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private promptService: PromptService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private socketService: SocketService,
        private updatesService: UpdatesService,
        private userService: UserService,
    ) {
        super();

        // IMPORTANT: Validate whether we have the required data to load.
        if (!this.licenceService.licenceObj) {
            this.userService.logout();
        }

        // TODO waiting for license service update.
        // EnduranceMenu.menuSubTitle = this.licenceService.getApplianceModelName();
        this.menuConfig = this.rbacService.menu;

        this.setUpApplicationData();
    }

    public ngOnInit(): void {
        // Setup socket listener's
        this.socketListeners();
    }

    /**
     * Logout of the user interface
     */
    public logout(): void {
        this.modalService.closeAll();
        this.updatesService.stopUpdateChecks();
        this.userService.logout();
    }

    /**
     * Navigate to pulseHA page.
     * @param link
     */
    public navigateToHAPage(link: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(link)).then().catch();
    }

    /**
     * Set up application data.
     * @private
     */
    private setUpApplicationData(): void {
        const applianceId: string = this.userService.getUser('applianceId');
        const nodeState: any = this.route.snapshot.data['nodeState'];
        const pulseHaServices: any = this.route.snapshot.data['pulseHaState'];
        this.clusterService.initialiseClusterService(nodeState.nodes, nodeState.pulseha, applianceId);
        this.promptService.initialisePromptService(pulseHaServices, applianceId);

        this.nodeStatus = this.clusterService.localNode.status;
        this.clusterStatus = this.clusterService.pulseHaStatus['status'];
    }

    /**
     * Get hint text for cluster health.
     * @private
     */
    private getHintText(): string {
        return `${this.languageService.translate(this.nodeStatus)}: ${this.languageService.translate(this.clusterStatus)}`;
    }

    /**
     * Setup socket listeners
     * @private
     */
    private socketListeners(): void {
        this.socketService.on('status')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (response: any): void => {
                    if (response.data?.pulseha) {
                        this.clusterService.pulseHaStatus = response.data.pulseha;
                    }

                    if (response.data?.services) {
                        this.promptService.nodeServiceStatuses = response.data.services;
                    }

                    if (response.data?.lock) {
                        this.lockService.locks = response.data.lock;
                    }

                    if (response.data?.update) {
                        this.updatesService.updateStatus = response.data.update;
                    }

                    this.clusterStatus = this.clusterService.getClusterHealth();
                    this.hintText = this.getHintText();
                }
            );
    }
}
